import React, { useEffect, useMemo } from "react";
import { Box, Typography, Tooltip, Stack, CircularProgress } from "@mui/material";
import { Handle, Position, useStore } from "@xyflow/react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorIcon from "@mui/icons-material/Error";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { NodeControls } from "./nodeControls";
import { useTheme } from "@mui/material/styles";

export const AiAgentNode = ({ id, data, selected, highlighted }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  // Get nodes and edges from store for connection validation
  const nodes = useStore((store) => store.nodes);
  const edges = useStore((store) => store.edges);
  const setEdges = useStore((store) => store.setEdges);
  const node = useStore((store) => store.nodes.find((n) => n.id === id));

  // Connection validation for inputs
  const connectionValidation = useMemo(() => {
    if (!edges || !Array.isArray(edges)) {
      return {
        chatModel: false,
        outputParser: false,
        memory: false,
        mainInput: false,
      };
    }

    const chatModelConnected = edges.some((e) => e.target === id && e.targetHandle === "chatModel");
    const outputParserConnected = edges.some(
      (e) => e.target === id && e.targetHandle === "outputParser"
    );
    const memoryConnected = edges.some((e) => e.target === id && e.targetHandle === "memory");
    const hasMainInput = edges.some((e) => e.target === id && e.targetHandle === "input");

    return {
      chatModel: chatModelConnected,
      outputParser: outputParserConnected,
      memory: memoryConnected,
      mainInput: hasMainInput,
    };
  }, [edges, id]);

  // Settings validation
  const settingsValidation = useMemo(() => {
    const settings = data.output?.settings || {};

    // Check required fields
    const hasSystemPrompt = settings.systemPrompt && settings.systemPrompt.trim().length > 0;
    const hasUserPrompt = settings.userPrompt && settings.userPrompt.trim().length > 0;
    const hasAgentName = settings.agentName && settings.agentName.trim().length > 0;

    // If no output parser connected, we need output format settings
    const hasOutputSettings = !connectionValidation.outputParser ? settings.outputFormat : true;

    return {
      hasRequiredSettings: hasSystemPrompt || hasUserPrompt,
      hasAgentName,
      hasOutputSettings,
      isFullyConfigured: hasSystemPrompt && hasUserPrompt && hasAgentName && hasOutputSettings,
    };
  }, [data.output?.settings, connectionValidation]);

  // Execution validation - chatModel is required
  const canExecute = useMemo(() => {
    return (
      connectionValidation.chatModel &&
      settingsValidation.hasRequiredSettings &&
      settingsValidation.hasOutputSettings &&
      connectionValidation.mainInput
    );
  }, [connectionValidation, settingsValidation]);

  // Status-based styling (following defaultNode pattern)
  const getStatusStyling = () => {
    const baseColor = selected ? "#27BBD9" : highlighted ? "#70C930" : "#666";
    const currentStatus = data.status || node?.status;

    switch (currentStatus) {
      case "running":
        return {
          borderColor: theme.palette.warning.main,
          glowColor: `${theme.palette.warning.main}30`,
          statusColor: theme.palette.warning.main,
          statusLabel: "Running",
        };
      case "pending":
        return {
          borderColor: theme.palette.info.main,
          glowColor: `${theme.palette.info.main}30`,
          statusColor: theme.palette.info.main,
          statusLabel: "Pending",
        };
      case "completed":
        return {
          borderColor: theme.palette.success.main,
          glowColor: `${theme.palette.success.main}30`,
          statusColor: theme.palette.success.main,
          statusLabel: "Completed",
        };
      case "error":
        return {
          borderColor: theme.palette.error.main,
          glowColor: `${theme.palette.error.main}30`,
          statusColor: theme.palette.error.main,
          statusLabel: "Error",
        };
      case "cancelled":
        return {
          borderColor: theme.palette.grey[500],
          glowColor: `${theme.palette.grey[500]}30`,
          statusColor: theme.palette.grey[500],
          statusLabel: "Cancelled",
        };
      default:
        return {
          borderColor: baseColor,
          glowColor: "transparent",
          statusColor: null,
          statusLabel: null,
        };
    }
  };

  const statusStyling = getStatusStyling();

  // Status badge renderer (following defaultNode pattern)
  const renderStatusBadge = () => {
    const currentStatus = data.status || node?.status;
    if (!currentStatus || currentStatus === "idle") return null;

    const getStatusIcon = () => {
      switch (currentStatus) {
        case "running":
          return <CircularProgress size={14} sx={{ color: statusStyling.statusColor }} />;
        case "pending":
          return <PlayArrowIcon sx={{ fontSize: 16, color: statusStyling.statusColor }} />;
        case "completed":
          return <CheckCircleIcon sx={{ fontSize: 16, color: statusStyling.statusColor }} />;
        case "error":
          return <ErrorIcon sx={{ fontSize: 16, color: statusStyling.statusColor }} />;
        case "cancelled":
          return <CancelIcon sx={{ fontSize: 16, color: statusStyling.statusColor }} />;
        default:
          return null;
      }
    };

    return (
      <Box
        sx={{
          position: "absolute",
          top: -8,
          right: -8,
          backgroundColor: "#414243",
          borderRadius: "50%",
          border: `2px solid ${statusStyling.statusColor}`,
          width: 24,
          height: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
          boxShadow: `0 2px 8px ${statusStyling.glowColor}`,
          transition: "all 0.2s ease-in-out",
        }}
        role="status"
        aria-label={statusStyling.statusLabel}
        title={statusStyling.statusLabel}
      >
        {getStatusIcon()}
      </Box>
    );
  };

  // Render node messages (validation feedback)
  const renderNodeMessages = () => {
    const messages = [];

    // Connection validation messages - chatModel is required
    if (!connectionValidation.chatModel) {
      messages.push({
        color: "orange",
        message: "⚠ Chat model required but not connected",
      });
    } else {
      messages.push({
        color: "lightgreen",
        message: "✔ Chat model connected",
      });
    }

    if (!connectionValidation.mainInput) {
      messages.push({
        color: "orange",
        message: "⚠ No input data connected",
      });
    }

    // Output parser messages
    if (!connectionValidation.outputParser) {
      const settings = data.output?.settings || {};
      if (!settings.outputFormat) {
        messages.push({
          color: "orange",
          message: "⚠ Output parser or format settings required",
        });
      } else {
        messages.push({
          color: "lightgreen",
          message: `✔ Using ${settings.outputFormat} output format`,
        });
      }
    } else {
      messages.push({
        color: "lightgreen",
        message: "✔ External output parser connected",
      });
    }

    // Memory connection messages
    if (connectionValidation.memory) {
      messages.push({
        color: "lightgreen",
        message: "✔ Memory connected",
      });
    }

    // Settings validation messages
    if (!settingsValidation.hasRequiredSettings) {
      messages.push({
        color: "orange",
        message: "⚠ Prompts not configured",
      });
    } else if (settingsValidation.isFullyConfigured) {
      messages.push({
        color: "lightgreen",
        message: "✔ Agent fully configured",
      });
    }

    return (
      <Box sx={{ mt: 1 }}>
        {messages.map((msg, idx) => (
          <Typography key={idx} variant="subtitle2" sx={{ fontSize: 11, mt: 0, color: msg.color }}>
            {msg.message}
          </Typography>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#414243",
        borderRadius: 2,
        padding: 2,
        position: "relative",
        minWidth: 240,
        border: `2px solid ${statusStyling.borderColor}`,
        color: "#fff",
        fontFamily: "Roboto, sans-serif",
        userSelect: "none",
        boxShadow:
          statusStyling.glowColor !== "transparent"
            ? `0 0 12px ${statusStyling.glowColor}`
            : "none",
        transition: "border 0.2s, box-shadow 0.2s",
        "&:hover .node-controls": {
          opacity: 1,
          pointerEvents: "auto",
        },
      }}
    >
      {/* Status Badge */}
      {renderStatusBadge()}

      {/* Main Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        style={{
          backgroundColor: connectionValidation.mainInput ? "#00C49F" : "#999",
          width: 16,
          height: 16,
          borderRadius: "50%",
          left: -9,
          top: "50%",
          transform: "translateY(-50%)",
          border: "2px solid white",
          cursor: "pointer",
          zIndex: 10,
        }}
      />

      {/* Bottom handles for AI agent specific connections */}
      <Handle
        type="target"
        position={Position.Bottom}
        id="chatModel"
        style={{
          backgroundColor: connectionValidation.chatModel ? "#00C49F" : "#888",
          width: 14,
          height: 14,
          borderRadius: 0,
          transform: "rotate(45deg)",
          border: "2px solid white",
          left: "calc(50% - 30px)",
          bottom: -8,
          cursor: "pointer",
          zIndex: 10,
        }}
        title="Chat Model (Required)"
      />

      <Handle
        type="target"
        position={Position.Bottom}
        id="memory"
        style={{
          backgroundColor: connectionValidation.memory ? "#00C49F" : "#888",
          width: 14,
          height: 14,
          borderRadius: 0,
          transform: "rotate(45deg)",
          border: "2px solid white",
          left: "calc(50%)",
          bottom: -8,
          cursor: "pointer",
          zIndex: 10,
        }}
        title="Memory (Optional)"
      />

      <Handle
        type="target"
        position={Position.Bottom}
        id="outputParser"
        style={{
          backgroundColor: connectionValidation.outputParser ? "#00C49F" : "#888",
          width: 14,
          height: 14,
          borderRadius: 0,
          transform: "rotate(45deg)",
          border: "2px solid white",
          left: "calc(50% + 30px)",
          bottom: -8,
          cursor: "pointer",
          zIndex: 10,
        }}
        title="Output Parser (Optional)"
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        style={{
          backgroundColor: "#00C49F",
          width: 18,
          height: 18,
          borderRadius: "50%",
          right: -9,
          top: "50%",
          transform: "translateY(-50%)",
          border: "2px solid white",
          cursor: "pointer",
          zIndex: 10,
        }}
      />

      {/* Node Controls (following defaultNode pattern) */}
      {node?.status !== "running" && (
        <NodeControls
          nodeId={id}
          nodeStatus={node?.status}
          onSettingsClick={() => data.onSettingsClick?.(id)}
          {...(canExecute ? { onRun: () => data.onRun?.(id) } : {})}
          {...(data.onDisable ? { onDisable: () => data.onDisable?.(id) } : {})}
        />
      )}

      {/* Header with icon and title */}
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <SmartToyIcon sx={{ color: "#00C49F" }} />
        <Typography sx={{ fontWeight: "bold", fontSize: 16 }}>
          {data.output?.settings?.agentName || data.label || "AI Agent"}
        </Typography>

        {/* Execution Output Indicator */}
        {data.execution?.success && data.execution?.output && (
          <Box
            sx={{
              backgroundColor: "#4caf50",
              borderRadius: "50%",
              width: 8,
              height: 8,
              flexShrink: 0,
            }}
            title="Node has execution output data"
          />
        )}
      </Box>

      {/* Role/Description Display */}
      {data.output?.settings?.role && (
        <Typography sx={{ fontSize: 12, color: "gray", mb: 1 }}>
          Role:{" "}
          {data.output.settings.role === "custom"
            ? data.output.settings.customRole || "Custom"
            : data.output.settings.role}
        </Typography>
      )}

      {/* Status Display Section (following defaultNode pattern) */}
      {node?.status !== "running" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>{renderNodeMessages()}</Box>
      ) : (
        <Box sx={{ mt: 1, height: 22, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
              opacity: 1,
            }}
          >
            <CircularProgress size={14} sx={{ color: "#FF6B35" }} />
            <Typography fontSize={11} sx={{ color: "#FF6B35", fontWeight: "medium" }}>
              {data.statusMessage || "Processing..."}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Warning icon for critical missing configuration */}
      {!connectionValidation.chatModel && (
        <Tooltip title="Missing required connection: Chat Model">
          <ErrorOutlineIcon
            sx={{
              color: "#ff6b6b",
              position: "absolute",
              top: 8,
              right: 32, // Offset to avoid status badge
              userSelect: "none",
              pointerEvents: "auto",
              cursor: "default",
              fontSize: 18,
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};
